<template>
  <div class="auth-container">
    <div class="auth-card">
      <div class="loading-container">
        <div class="logo-container">
          <img src="@/assets/query-img.svg" :alt="$t('auth.common.logoAlt')" />
        </div>

        <div v-if="!error" class="success-state">
          <h5 class="form-title">{{ $t('auth.oauthCallback.successTitle') }}</h5>
          <div class="spinner"></div>
          <p class="loading-text">{{ $t('auth.oauthCallback.loadingText') }}</p>
        </div>

        <div v-else class="error-state">
          <h5 class="form-title error-title">{{ $t('auth.oauthCallback.errorTitle') }}</h5>
          <p class="error-message">{{ error }}</p>
          <p class="redirect-text">{{ $t('auth.oauthCallback.redirectText') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from '@/supabase/supabaseClient';

export default {
  name: 'OAuthCallback',
  data() {
    return {
      error: null,
      authListener: null
    };
  },

  async mounted() {

    try {
      // 🔹 Méthode 1 : Si il y a un hash avec access_token, on attend que Supabase le traite
      if (window.location.hash && window.location.hash.includes('access_token')) {

        // Écoute les changements d'état d'auth
        this.authListener = supabase.auth.onAuthStateChange(async (event, session) => {
          if (session?.user) {
            // Session ready
          }

          if (event === 'SIGNED_IN' && session) {

            // Créer le profil utilisateur si nécessaire
            await this.ensureUserProfile(session.user);

            // Petit délai pour l'UX
            await new Promise(resolve => setTimeout(resolve, 500));

            // Redirection vers dashboard
            this.$router.replace({ path: '/dashboardPage' });
          } else if (event === 'SIGNED_OUT') {
            console.error('User signed out unexpectedly');
            this.error = this.$t('auth.oauthCallback.errors.signedOut');
            this.redirectToLogin();
          }
        });

        // Timeout de sécurité : si après 10 secondes on n'a toujours pas de session, erreur
        setTimeout(() => {
          if (!this.error) {
            this.checkSessionOrFail();
          }
        }, 10000);

      } else {
        // 🔹 Méthode 2 : Pas de hash, vérifier directement la session
        await this.checkSessionOrFail();
      }

    } catch (error) {
      console.error('Error in OAuth callback:', error);
      this.error = error.message || this.$t('auth.oauthCallback.errors.generic');
      this.redirectToLogin();
    }
  },

  beforeUnmount() {
    // Nettoyer le listener
    if (this.authListener) {
      this.authListener.data.subscription.unsubscribe();
    }
  },

  methods: {
    async checkSessionOrFail() {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        console.error('Session error:', error);
        throw error;
      }

      if (data.session) {
        await this.ensureUserProfile(data.session.user);
        await new Promise(resolve => setTimeout(resolve, 500));
        this.$router.replace({ path: '/dashboardPage' });
      } else {
        console.error('No session found');
        throw new Error(this.$t('auth.oauthCallback.errors.noSession'));
      }
    },

    // eslint-disable-next-line no-unused-vars
    async ensureUserProfile(user) {

      const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';

      try {
        // Récupérer le token d'accès
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;

        if (!accessToken) {
          console.error('🔐 ❌ No access token available');
          return;
        }

        // Appeler le backend pour assigner le cabinet SSO (utilise service_role, bypass RLS)
        const response = await fetch(`${API_URL}/copilot/sso-assign-cabinet`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          }
        });

        const result = await response.json();

        if (result.assigned) {
          // Cabinet assigned successfully
        } else if (result.reason === 'Cabinet does not allow auto-creation') {
          this.error = this.$t('auth.oauthCallback.errors.cabinetRestricted');
          this.redirectToLogin();
          return;
        } else {
          // Other reason - continue without blocking
        }


      } catch (err) {
        console.error('🔐 ❌ Error in ensureUserProfile:', err);
        // Ne pas bloquer la connexion si l'assignation échoue
      }
    },

    redirectToLogin() {
      setTimeout(() => {
        this.$router.replace({ path: '/loginPage' });
      }, 3000);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap');

.auth-container {
  display: flex;
  justify-content: center;
  background: url("@/assets/sign-In-bg.png");
  background-size: cover;
  background-position: center;
  min-height: 100vh;
  font-family: 'Roboto', sans-serif;
}

.auth-card {
  width: 500px;
  padding: 0 80px;
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.loading-container {
  text-align: center;
  padding: 40px 0;
}

.logo-container {
  width: 100%;
  height: 130px;
  background: black;
  border-radius: 0 0 20px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 40px;
}

.form-title {
  text-align: center;
  font-weight: bold;
  margin-top: 0;
  margin-bottom: 30px;
  font-size: 20px;
  color: #333;
}

.error-title {
  color: #ff5252;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6c63ff;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin-top: 20px;
}

.success-state {
  padding: 20px 0;
}

.error-state {
  padding: 20px 0;
}

.error-message {
  color: #ff5252;
  font-size: 14px;
  margin: 20px 0;
  padding: 15px;
  background-color: #fff5f5;
  border-radius: 8px;
  border: 1px solid #ffcdd2;
}

.redirect-text {
  color: #999;
  font-size: 12px;
  margin-top: 15px;
}

@media (max-width: 768px) {
  .auth-card {
    width: 90%;
    padding: 0 30px;
  }

  .logo-container {
    height: 100px;
  }

  .form-title {
    font-size: 18px;
  }
}
</style>
